import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import {
  CreateCreditLineBody,
  DecisionEngineResponse,
  Evaluation,
  NodesAvailableResponse,
  PaginatedEvaluationsResponse,
} from './types';

export const fetchCreditLineEvaluations = async (
  dispatch: Dispatch,
  params: {
    page: number;
    pageSize: number;
    identifier: string | null;
  },
): Promise<PaginatedEvaluationsResponse> => {
  const { page, pageSize, identifier } = params;
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    ...(identifier ? { identifier } : {}),
  }).toString();

  const response = await requestHandler({
    method: 'get',
    url: `internal/decisionEngine/product/creditLine?${query}`,
    dispatch,
  });
  if (response) return response.data;
  return {
    data: [],
    pagination: { page, pageSize, total: 0, totalPages: 0 },
  };
};

export const fetchCreditLineEvaluation = (
  dispatch: Dispatch,
  id: number | undefined,
) =>
  useQuery({
    queryKey: ['decisionEngine', 'creditLine', 'evaluation', id],
    queryFn: async (): Promise<
      | (Evaluation & { businessName: string; businessIdentifier: string })
      | undefined
    > => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/decisionEngine/product/creditLine/${id}`,
        dispatch,
      });
      if (response) return response.data;
      return undefined;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

// DEPRECATED (New: getDecisionEngines)
export const fetchDecisionEnginesAvailables = async (
  dispatch: Dispatch,
  revisionObjective: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/decisionEngine/availables?revisionObjective=${revisionObjective}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getDecisionEngines = (
  dispatch: Dispatch,
  revisionObjective: string,
) =>
  useQuery({
    queryKey: ['decisionEngine', 'availables', revisionObjective],
    queryFn: async (): Promise<DecisionEngineResponse[] | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/decisionEngine/availables?revisionObjective=${revisionObjective}`,
        dispatch,
      });
      if (response) return response.data;
      return [];
    },
    enabled: !!revisionObjective,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

export const createCreditLineEvaluation = (dispatch: Dispatch) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['decisionEngine', 'creditLine', 'create'],
    mutationFn: async (payload: CreateCreditLineBody) => {
      const response = await requestHandler({
        method: 'post',
        url: 'internal/decisionEngine/creditLine',
        data: payload,
        dispatch,
      });
      if (response) return response.data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['decisionEngine', 'creditLine', 'evaluations'],
      });
    },
  });
};

export const selectDecisionEngineAsDefault = async (
  dispatch: Dispatch,
  engineId: number,
) => {
  const response = await requestHandler({
    method: 'put',
    url: `internal/decisionEngine/${engineId}/default`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCreditLineFactoringSelectedDecisionEngines = async (
  dispatch: Dispatch,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/decisionEngine/creditLine/factoring/selected`,
    dispatch,
  });
  if (response) return response.data;
};

export const getNodesAvailable = (
  dispatch: Dispatch,
  revisionObjective: string | null,
) =>
  useQuery({
    queryKey: ['decisionEngine', 'nodesAvailable', revisionObjective],
    queryFn: async (): Promise<NodesAvailableResponse | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/decisionNode/availableNodes?revisionObjective=${revisionObjective}`,
        dispatch,
      });
      if (response) return response.data;
    },
    enabled: !!revisionObjective,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    retry: 1,
  });

export const getRequestEvaluations = (
  dispatch: Dispatch,
  data: { requestType: string; requestId: number | null },
) =>
  useQuery({
    queryKey: [
      'decisionEngine',
      'requestEvaluations',
      data.requestType,
      data.requestId,
    ],
    queryFn: async (): Promise<Record<string, Evaluation[]>> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/decisionEngine/requestType/${data.requestType}/requestId/${data.requestId}`,
        dispatch,
      });
      if (response) return response.data;
      return {};
    },
    enabled: !!data.requestType && !!data.requestId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 5,
    retry: 1,
  });
